/**
 * This is the client-side entrypoint for your tRPC API. It is used to create the `api` object which
 * contains the Next.js App-wrapper, as well as your type-safe React Query hooks.
 *
 * We also create a few inference helpers for input and output types.
 */
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { createWSClient, httpLink, loggerLink, splitLink, wsLink } from '@trpc/client';
import { createTRPCNext } from '@trpc/next';
import { ssrPrepass } from '@trpc/next/ssrPrepass';
import { getAuth } from 'firebase/auth';
import superjson from 'superjson';

import type { AppRouter } from '@poseidon/server/api';

import { env } from '~/env';

export function getBaseUrl() {
  if (typeof window !== 'undefined') return ''; // browser should use relative url
  if (env.NEXT_PUBLIC_BACKEND_URL) return env.NEXT_PUBLIC_BACKEND_URL; // SSR should use vercel url
  return `http://localhost:3000`; // dev SSR should use localhost
}

function getLastLink() {
  if (typeof window === 'undefined') {
    return getHttpLink();
  }

  return splitLink({
    condition: (op) => op.type === 'subscription',
    true: getWSLink(),
    false: getHttpLink(),
  });
}

function getHttpLink() {
  return httpLink({
    url: `${getBaseUrl()}/api/trpc`,
    // maxURLLength: 2000,
    headers: async () => {
      const authToken = await getAuth().currentUser?.getIdToken();
      if (!authToken) return {};
      return {
        authorization: `Bearer ${authToken}`,
      };
    },
    transformer: superjson,
  });
  // return httpBatchLink({
  //   url: `${getBaseUrl()}/api/trpc`,
  //   headers: async () => {
  //     const authToken = await getAuth().currentUser?.getIdToken();
  //     if (!authToken) return {};
  //     return {
  //       authorization: `Bearer ${authToken}`,
  //     };
  //   },
  //   transformer: superjson,
  // });
}

let wsClient: ReturnType<typeof createWSClient> | undefined;
function getWSLink() {
  if (wsClient == null) {
    /**
     * If we're on HTTPS, use WSS, else use WS
     */
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const url =
      // eslint-disable-next-line no-restricted-properties
      process.env.NODE_ENV === 'development'
        ? `${protocol}://localhost:${env.NEXT_PUBLIC_DEV_WS_PORT}`
        : `${protocol}://${window.location.host}/api/ws`;

    wsClient = createWSClient({
      url,
      lazy: { enabled: true, closeMs: 0 },
      onClose(cause) {
        console.log('ws closed:', cause);
      },
      onOpen() {
        console.log('ws opened');
      },
    });
  }

  return wsLink<AppRouter>({
    client: wsClient,
    /**
     * Transformer used for data de-serialization from the server.
     *
     * @see https://trpc.io/docs/data-transformers
     */
    transformer: superjson,
  });
}

/** A set of type-safe react-query hooks for your tRPC API. */
export const api = createTRPCNext<AppRouter>({
  config: () => {
    return {
      queryClientConfig: {
        defaultOptions: {
          queries: {
            // added this flag for development purposes, to avoid refetching on window focus
            // DEFAULT behaviour - (if this is not set), the page will refetch on window focus
            refetchOnWindowFocus: env.NEXT_PUBLIC_DISABLE_REFETCH_ON_WINDOW_FOCUS !== true,
          },
        },
      },

      /**
       * Links used to determine request flow from client to server.
       *
       * @see https://trpc.io/docs/links
       */
      links: [
        loggerLink({
          enabled: (opts) =>
            // eslint-disable-next-line no-restricted-properties
            process.env.NODE_ENV === 'development' || (opts.direction === 'down' && opts.result instanceof Error),
        }),

        getLastLink(),
      ],
    };
  },
  /**
   * Whether tRPC should await queries when server rendering pages.
   *
   * @see https://trpc.io/docs/nextjs#ssr-boolean-default-false
   */
  ssr: true,
  ssrPrepass: ssrPrepass,
  transformer: superjson,
});

/**
 * Inference helper for inputs.
 *
 * @example type HelloInput = RouterInputs['example']['hello']
 */
export type RouterInputs = inferRouterInputs<AppRouter>;

/**
 * Inference helper for outputs.
 *
 * @example type HelloOutput = RouterOutputs['example']['hello']
 */
export type RouterOutputs = inferRouterOutputs<AppRouter>;
